import { Instagram, Music2, Youtube, User } from "lucide-react";
import { CreatorScoreBadge } from "../score/CreatorScoreBadge";

import { cn } from "@/marketing/lib/utils";
import { getCreatorCardSubtext } from "@/marketing/lib/creatorCardSubtext";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";
import { CreatorCardTags } from "./CreatorCardTags";

function PlatformIcon({ platform }: { platform?: string | undefined }) {
  if (platform === "tiktok")
    return <Music2 className="tw-h-3.5 tw-w-3.5 tw-text-muted-foreground" />;
  if (platform === "instagram")
    return <Instagram className="tw-h-3.5 tw-w-3.5 tw-text-muted-foreground" />;
  if (platform === "youtube")
    return <Youtube className="tw-h-3.5 tw-w-3.5 tw-text-muted-foreground" />;
  return <User className="tw-h-3.5 tw-w-3.5 tw-text-muted-foreground" />;
}

interface CreatorCardProps {
  campaignId: string;
  pipelineDefinition: CampaignPipelineDefinition;
  creator: CampaignInfluencer;
  onClick: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
}

export function CreatorCard({
  campaignId,
  pipelineDefinition,
  creator,
  onClick,
  onDragStart,
  onDragEnd,
  isDragging,
}: CreatorCardProps) {
  const handle = creator.handle ?? creator.influencerId.slice(0, 8);
  const initials =
    handle
      .replace(/[^a-zA-Z]/g, "")
      .slice(0, 2)
      .toUpperCase() || "??";
  const subtext = getCreatorCardSubtext(pipelineDefinition, creator);
  const fastTrackBadge = creator.fastTrack && !creator.rejected;

  return (
    <div
      className={cn(
        "tw-group tw-relative tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-card tw-transition-shadow hover:tw-shadow-sm",
        creator.rejected && "tw-border-l-[3px] tw-border-l-rose-500 tw-opacity-80",
        isDragging && "tw-opacity-50",
      )}
    >
      <button
        type="button"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("text/influencerId", creator.influencerId);
          e.dataTransfer.effectAllowed = "move";
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        onClick={onClick}
        className="tw-w-full tw-cursor-grab tw-px-2.5 tw-pb-1.5 tw-pt-2.5 tw-text-left active:tw-cursor-grabbing"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-[10px] tw-font-semibold tw-text-foreground/70">
            {initials}
          </div>
          <div className="tw-min-w-0 tw-flex-1">
            <div className="tw-truncate tw-text-[13px] tw-font-medium tw-leading-tight">
              @{handle}
            </div>
            <div className="tw-truncate tw-text-[11px] tw-text-muted-foreground">
              {creator.influencerId}
            </div>
          </div>
          <PlatformIcon platform={creator.platform} />
        </div>
        <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-muted-foreground">
          <span className="tw-flex-1 tw-truncate">{subtext}</span>
          {fastTrackBadge && (
            <span className="tw-rounded tw-bg-violet-100 tw-px-1 tw-py-px tw-text-[10px] tw-font-semibold tw-text-violet-700">
              FT
            </span>
          )}
        </div>
      </button>
      <div className="tw-px-2.5 tw-pb-2.5 tw-pt-0.5">
        <div className="tw-mb-2"><CreatorScoreBadge creator={creator} /></div>
        <CreatorCardTags campaignId={campaignId} creator={creator} />
      </div>
    </div>
  );
}
